import { i18n, type Messages } from "@lingui/core";

// The ?lingui query tells the Lingui Vite plugin to compile the JSON catalogs
const localeModules = import.meta.glob<{ messages: Messages }>(
  "./locales/*.json",
  { query: "?lingui" },
);

export const locales = Object.keys(localeModules).flatMap((path) => {
  const match = path.match(/\.\/locales\/(.+)\.json/);
  return match ? [match[1]] : [];
});

const defaultLocale = "en";

const LOCALE_STORAGE_KEY = "seerlinq.locale";

/**
 * Locales offered in the UI language switcher — only those with complete
 * translations. `sv` exists in the catalog as an untranslated placeholder;
 * add it here once translated. (`locales` above, derived from the catalog
 * glob, still lists all of them so their catalogs keep compiling.)
 */
export const switchableLocales = ["en", "de", "it", "pl", "sk"] as const;

/** Native display names, keyed by locale code. */
export const localeNames: Record<string, string> = {
  en: "English",
  de: "Deutsch",
  it: "Italiano",
  sk: "Slovenčina",
  pl: "Polski",
  sv: "Svenska",
};

export async function setLocale(locale: string) {
  const resolved = locales.includes(locale) ? locale : defaultLocale;

  const loader = localeModules[`./locales/${resolved}.json`];
  if (!loader) return;

  const { messages } = await loader();
  i18n.load(resolved, messages);
  i18n.activate(resolved);
}

/** Remember the user's choice so it survives reloads and future sessions. */
export function persistLocale(locale: string) {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Storage can be unavailable (private mode, disabled cookies) — the choice
    // just won't persist, which is acceptable.
  }
}

export function getInitialLocale(): string {
  // An explicit `?locale=` wins (shareable/testing links); otherwise fall back
  // to the persisted choice, then the default. `setLocale` guards unknown
  // values, so a stale stored code can't break startup.
  const fromQuery = new URLSearchParams(window.location.search).get("locale");
  if (fromQuery) return fromQuery;

  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored) return stored;
  } catch {
    // Ignore storage errors and use the default.
  }

  return defaultLocale;
}
